import { useEffect, useState } from 'react';
import { AlertCircle, Check, CloudUpload, Info, MapPinPlus, X } from 'lucide-react';
import { Dialog, DialogPortal, DialogOverlay } from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PROVINCE_YN, type AppendAddressRequest, type ProvinceYn } from '../types/salesOrder.types';

interface Props {
  open: boolean;
  customerId: string;
  customerName: string;
  /** Order-level context — shown in the strip, seeds City and the Province suggestion. */
  orderStateProvinceName: string | null;
  orderCityTownName: string | null;
  /** Set when a previous save was rejected: NetSuite's raw message + the attempt count. */
  failure: { message: string; attempt: number } | null;
  onSave: (request: AppendAddressRequest) => void;
  onClose: () => void;
}

// 86d405z28 — create an address on the customer's NetSuite record without leaving the SO form.
//
// The write is IRREVERSIBLE (NetSuite exposes no per-address DELETE) so this dialog is
// deliberately hard to leave by accident and states the one-time nature plainly. It never saves
// on its own: the parent owns the call, and it is made from a button press only.
export function AddNewAddressDialog({
  open,
  customerName,
  orderStateProvinceName,
  orderCityTownName,
  failure,
  onSave,
  onClose,
}: Props) {
  const [label, setLabel] = useState('');
  const [addressee, setAddressee] = useState('');
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [addr3, setAddr3] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [provinceYn, setProvinceYn] = useState<ProvinceYn>(PROVINCE_YN.NO);
  // Tracked SEPARATELY from the value: once the coordinator has answered, changing the order's
  // State / Province must not silently move their answer underneath them.
  const [provinceTouched, setProvinceTouched] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // NCR / Metro Manila → NO, anywhere else → YES. A suggestion, not an inference: the order's
  // province is where the ORDER goes, while the address belongs to the customer, so they can
  // legitimately differ.
  const suggested: ProvinceYn = /metro manila|ncr/i.test(orderStateProvinceName ?? '')
    ? PROVINCE_YN.NO
    : PROVINCE_YN.YES;

  // Re-seed on every open. The one exception is a reopen after a rejected save (`failure` set),
  // where losing what was typed is the whole thing this feature exists to prevent.
  useEffect(() => {
    if (!open || failure) return;
    setLabel('');
    setAddressee(customerName);
    setAddr1('');
    setAddr2('');
    setAddr3('');
    setCity(orderCityTownName ?? '');
    setZip('');
    setProvinceYn(suggested);
    setProvinceTouched(false);
    setConfirmingDiscard(false);
    setShowErrors(false);
    // `suggested` is derived from orderStateProvinceName, which is in the deps already.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerName, orderCityTownName, orderStateProvinceName, failure]);

  // Follow the order's province only while the coordinator hasn't answered for themselves.
  useEffect(() => {
    if (!provinceTouched) setProvinceYn(suggested);
  }, [suggested, provinceTouched]);

  const dirty =
    label !== '' ||
    addr1 !== '' ||
    addr2 !== '' ||
    addr3 !== '' ||
    zip !== '' ||
    addressee !== customerName;

  function requestClose() {
    if (dirty && !confirmingDiscard) {
      setConfirmingDiscard(true);
      return;
    }
    setConfirmingDiscard(false);
    onClose();
  }

  // NetSuite requires exactly these two; Country is fixed and Province always holds a value.
  const missingAddressee = addressee.trim() === '';
  const missingAddr1 = addr1.trim() === '';

  function handleSave() {
    if (missingAddressee || missingAddr1) {
      setShowErrors(true);
      return;
    }
    onSave({
      label: label.trim() || undefined,
      addressee: addressee.trim(),
      addr1: addr1.trim(),
      addr2: addr2.trim() || null,
      addr3: addr3.trim() || null,
      city: city.trim() || null,
      zip: zip.trim() || null,
      countryId: 'PH',
      provinceYn,
    });
  }

  const provinceOptions: Array<{ value: ProvinceYn; text: string }> = [
    { value: PROVINCE_YN.YES, text: 'YES — out in the province' },
    { value: PROVINCE_YN.NO, text: 'NO — within Metro Manila' },
    { value: PROVINCE_YN.NA, text: 'N/A' },
  ];

  return (
    <Dialog open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            requestClose();
          }}
          className={cn(
            'fixed left-[50%] top-[50%] z-50 flex max-h-[86vh] w-full translate-x-[-50%] translate-y-[-50%]',
            'flex-col overflow-hidden rounded-xl border bg-card shadow-lg sm:max-w-[760px]',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out',
          )}
        >
          {/* There is no app-wide TooltipProvider — every consumer wraps its own subtree (see
              PermissionsPanel). Without this, the two Tooltips below throw on mount and take the
              whole page down with them. */}
          <TooltipProvider>
          {/* Header */}
          <div className="flex items-start gap-3 border-b p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
              <MapPinPlus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-[15px] font-semibold">
                New shipping address
              </DialogPrimitive.Title>
              <p className="text-xs text-muted-foreground">
                Adds to {customerName}'s address book in NetSuite
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={requestClose}
                  aria-label="Close new address dialog"
                  title="Close without saving"
                  className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Close without saving</TooltipContent>
            </Tooltip>
          </div>

          {/* Context strip — the dialog covers the two fields it derives from. */}
          <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 border-b bg-muted/40 px-4 py-2 text-[11.5px] text-muted-foreground">
            <span className="font-semibold uppercase tracking-[.04em]">On this order</span>
            <span>
              State / Province{' '}
              <span className="font-semibold text-foreground">{orderStateProvinceName ?? '—'}</span>
            </span>
            <span>
              City / Town{' '}
              <span className="font-semibold text-foreground">{orderCityTownName ?? '—'}</span>
            </span>
          </div>

          {/* Body */}
          <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
            {failure && (
              <div className="rounded-md border border-destructive/35 bg-destructive/[.08] p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <div className="min-w-0 space-y-1.5">
                    <p className="text-[13px] font-semibold text-destructive">
                      NetSuite would not accept this address — reopened so you can fix it
                    </p>
                    {/* Verbatim, never paraphrased: coordinators forward these to whoever
                        maintains the NetSuite side. */}
                    <p className="whitespace-pre-wrap break-words font-mono text-[11.5px] text-foreground">
                      {failure.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Nothing was written — {customerName}'s address book is unchanged, and
                      everything you typed is still below. Fix it and try again; a retry creates one
                      address, never a second copy.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-[.05em] text-muted-foreground">
                Address record
              </span>
              <span className="text-[11px] text-muted-foreground">
                <span className="text-destructive">*</span> required by NetSuite
              </span>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-3">
              <Field label="Label" helper="Name shown in the address list.">
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. NORTH SITE GATE 2"
                />
              </Field>
              <Field
                label="Addressee"
                required
                helper="Prefilled with the customer — edit for a site or receiver."
                error={showErrors && missingAddressee ? 'Addressee is required' : undefined}
              >
                <Input
                  value={addressee}
                  onChange={(e) => setAddressee(e.target.value)}
                  placeholder="Who the delivery is addressed to"
                />
              </Field>

              <div className="col-span-2">
                <Field
                  label="Address 1"
                  required
                  error={showErrors && missingAddr1 ? 'Address 1 is required' : undefined}
                >
                  <Input
                    value={addr1}
                    onChange={(e) => setAddr1(e.target.value)}
                    placeholder="Street, building, barangay"
                  />
                </Field>
              </div>

              <Field label="Address 2">
                <Input value={addr2} onChange={(e) => setAddr2(e.target.value)} />
              </Field>
              <Field label="Address 3">
                <Input value={addr3} onChange={(e) => setAddr3(e.target.value)} />
              </Field>

              <Field
                label="City"
                helper="Free text on the address record — not the order's City / Town list. Prefilled from it; edit if they differ."
              >
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </Field>
              <Field label="Zip">
                <Input value={zip} onChange={(e) => setZip(e.target.value)} />
              </Field>

              <div className="col-span-2">
                <Field label="Country" required>
                  <Select value="PH" onValueChange={() => {}}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PH">Philippines</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            {/* Province (Y/N) — a suggestion, not an inference. */}
            <div className="space-y-2 rounded-md border bg-muted/45 p-2.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-medium">
                  Province (Y/N)
                  <span className="text-destructive"> *</span>
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What Province (Y/N) is for"
                      title="What Province (Y/N) is for"
                      className="text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  {/* Deliberately makes no claim about what consumes this field — nothing has
                      been shown to. It is stated only as what we know: NetSuite requires it. */}
                  <TooltipContent className="max-w-[260px]">
                    A required field on the address record — NetSuite will not save an address
                    without it.
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {provinceOptions.map((opt) => {
                  const selected = provinceYn === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setProvinceYn(opt.value);
                        setProvinceTouched(true);
                      }}
                      className={cn(
                        'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected
                          ? 'bg-secondary font-semibold text-secondary-foreground'
                          : 'border border-input bg-background hover:bg-muted',
                      )}
                    >
                      {selected && <Check className="h-[13px] w-[13px]" />}
                      {opt.text}
                    </button>
                  );
                })}
              </div>

              <p className="text-[11.5px] text-muted-foreground">
                {provinceTouched
                  ? `Changed from the suggested ${labelFor(suggested)}. The order's State / Province is ${orderStateProvinceName ?? '—'}.`
                  : `Suggested from the order's State / Province (${orderStateProvinceName ?? '—'}). Change it if the delivery site differs.`}
              </p>
            </div>

            {/* Write-once boundary. */}
            <div className="flex items-start gap-2 rounded-md border border-info/30 bg-info/[.07] p-3">
              <CloudUpload className="mt-0.5 h-4 w-4 shrink-0 text-info" />
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  Saving is a one-time write to NetSuite.
                </span>{' '}
                This adds the address to {customerName}'s record now — a separate step from creating
                the Sales Order. Creating, or retrying, the order later never writes it a second
                time.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center gap-2 border-t bg-muted/35 px-4 py-3">
            {confirmingDiscard ? (
              <>
                <AlertCircle className="h-4 w-4 text-warning" />
                <span className="text-[13px] font-medium">Discard this address?</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" onClick={() => setConfirmingDiscard(false)}>
                    Keep editing
                  </Button>
                  <Button
                    className="border border-destructive/40 bg-destructive/10 font-semibold text-destructive hover:bg-destructive/20"
                    onClick={() => {
                      setConfirmingDiscard(false);
                      onClose();
                    }}
                  >
                    Discard
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button variant="secondary" className="h-9 gap-1.5" onClick={handleSave}>
                  <CloudUpload className="h-[15px] w-[15px]" />
                  {failure ? 'Try saving again' : 'Save address to NetSuite'}
                </Button>
                <Button variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <span className="text-[11.5px] text-muted-foreground">
                  {failure
                    ? `Attempt ${failure.attempt}`
                    : 'Saves once, then selects it for this order.'}
                </span>
              </>
            )}
          </div>
          </TooltipProvider>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

function labelFor(value: ProvinceYn) {
  return value === PROVINCE_YN.YES ? 'YES' : value === PROVINCE_YN.NO ? 'NO' : 'N/A';
}

function Field({
  label,
  required,
  helper,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[13px] font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] leading-tight text-destructive">{error}</p>
      ) : helper ? (
        <p className="text-[11px] leading-tight text-muted-foreground">{helper}</p>
      ) : null}
    </div>
  );
}
